import cartIconQuantity from '#shared/state/CartIconQuantity';
import type { User } from '#shared/types/User';
import { useAnonId } from '~/composables/useAnonId';
import { sendDataLayerEvent } from '~/composables/useDataLayer';

const state = reactive<{
    loading: boolean;
    user: User | null | undefined;
}>({
    loading: true,
    user: undefined,
});

const userRequest = ref<Promise<void> | null>(null);
const { getOrCreateAnonId, destroyAnonId } = useAnonId();

const apiRequest = async (
    method: 'GET' | 'POST',
    endpoint: string,
    data: { [key: string]: any } | FormData = {},
) => {
    const csrfToken = await getCsrfToken();
    const anonId = getOrCreateAnonId();

    const headers: Record<string, string> = {
        Accept: 'application/json',
        'X-XSRF-TOKEN': csrfToken,
    };

    const payload =
        data instanceof FormData
            ? (() => {
                  if (anonId) data.append('anonId', anonId);
                  return data;
              })()
            : {
                  ...data,
                  anonId,
              };

    return await $fetch(`api/${endpoint}`, {
        method,
        baseURL: useRuntimeConfig().public.portalUrl,
        credentials: 'include',
        retry: false,
        headers,
        ['POST' === method ? 'body' : 'query']: payload,
    });
};

const fetchUser = async () => {
    if (!userRequest.value) {
        userRequest.value = $fetch('api/user', {
            baseURL: useRuntimeConfig().public.portalUrl,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        })
            .then((responseData: any) => {
                state.user = responseData.user;
                cartIconQuantity.value = responseData.cartItemsCount;
            })
            .finally(() => {
                state.loading = false;
            });
    }

    return userRequest.value;
};

const getCsrfToken = async (forceRefresh = false): Promise<string> => {
    let csrfToken = useCookie<string | null>('XSRF-TOKEN').value;
    if (csrfToken && !forceRefresh) {
        return csrfToken;
    }

    await $fetch('sanctum/csrf-cookie', {
        baseURL: useRuntimeConfig().public.portalUrl,
        credentials: 'include',
    });

    refreshCookie('XSRF-TOKEN');
    csrfToken = useCookie<string | null>('XSRF-TOKEN').value;

    if (!csrfToken) {
        throw new Error('The CSRF cookie was not set.');
    }

    return csrfToken;
};

const login = async (
    email: string,
    password: string,
    remember: boolean,
    companyInviteToken?: string,
) => {
    return await apiRequest('POST', 'login', {
        email,
        password,
        remember,
        token_invite: companyInviteToken,
    }).then((responseData: any) => {
        state.user = responseData.data.user;
        cartIconQuantity.value = responseData.data.cartItemsCount;
        sendDataLayerEvent('login');
        destroyAnonId();
        return Promise.resolve(responseData);
    });
};

const requestPasswordReset = async (email: string) => {
    return await apiRequest('POST', 'send-password-reset-email', {
        email,
    }).then((responseData: any) => {
        return Promise.resolve(responseData);
    });
};

const resetPassword = async (
    email: string,
    password: string,
    confirmPassword: string,
    token?: string,
) => {
    return await apiRequest('POST', 'reset-password', {
        email,
        password,
        password_confirmation: confirmPassword,
        token,
    }).then((responseData: any) => {
        return Promise.resolve(responseData);
    });
};

const logout = async () => {
    state.user = null;
    cartIconQuantity.value = 0;
    destroyAnonId();
    return apiRequest('POST', 'logout');
};

const register = async (
    firstName: string,
    lastName: string,
    company: string,
    zipCode: string,
    email: string,
    password: string,
    remember: boolean,
    emailOptIn: boolean,
    recaptchaToken?: string,
    companyInviteToken?: string,
) => {
    return await apiRequest('POST', 'register', {
        first_name: firstName,
        last_name: lastName,
        company,
        zip_code: zipCode,
        email,
        password,
        remember,
        email_optin: emailOptIn,
        recaptchaToken,
        token_invite: companyInviteToken,
    }).then((responseData: any) => {
        state.user = responseData.data.user;
        cartIconQuantity.value = responseData.data.user.cartItemsCount;
        sendDataLayerEvent('create_account');
        destroyAnonId();
        return Promise.resolve(responseData);
    });
};

export default function () {
    return {
        ...toRefs(state),
        apiRequest,
        fetchUser,
        getCsrfToken,
        login,
        logout,
        register,
        requestPasswordReset,
        resetPassword,
    };
}
